import { surveyScheduleWindow } from '@felix/domain';
import { Button, Drawer, Select, SimpleGrid, Stack, Textarea, TextInput } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import { type ReactNode, useEffect, useState } from 'react';
import { Controller, type FieldValues, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { useUnsavedChanges } from '../../shell-context.js';
import { ConfirmRemove } from './CertificateDrawer.js';
import classes from './Compliance.module.css';
import { useCertificateErrorText } from './errors.js';
import { useErrorOf, useSchemaForm } from './form-parts.js';
import { authorizationFormSchema, findingFormSchema, surveyFormSchema } from './forms.js';
import {
  type CeAuthorization,
  type CeAuthorizationInput,
  CLASS_FINDING_KINDS,
  type ClassFinding,
  type ClassFindingInput,
  type EquipmentChoice,
  type PersonChoice,
  SURVEY_KINDS,
  type SurveyInput,
  type SurveyKind,
  type VesselSurvey,
} from './types.js';

/** Ofis çekmecesinin çerçevesi: başlık, form, hata, sil (soft delete), vazgeç ve kaydet. */
function OfficeDrawer<V extends FieldValues>({
  opened,
  title,
  form,
  error,
  removeLabel,
  onRemove,
  onClose,
  onSubmit,
  children,
}: {
  opened: boolean;
  title: string;
  form: UseFormReturn<V>;
  error: unknown;
  removeLabel?: string | undefined;
  onRemove?: (() => void) | undefined;
  onClose: () => void;
  onSubmit: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const errorText = useCertificateErrorText();
  useUnsavedChanges(opened && form.formState.isDirty);
  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="md"
      title={<span className={classes.drawerTitle}>{title}</span>}
      closeButtonProps={{ 'aria-label': t('vesselCertificates.dialogs.close') }}
    >
      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit();
        }}
      >
        <Stack gap="sm">{children}</Stack>
        {error ? (
          <p className={classes.formError} role="alert">
            {errorText(error)}
          </p>
        ) : null}
        <div className={classes.dialogActions}>
          {onRemove && removeLabel ? (
            <Button
              variant="subtle"
              color="red"
              mr="auto"
              leftSection={<IconTrash size={15} aria-hidden />}
              onClick={onRemove}
            >
              {removeLabel}
            </Button>
          ) : null}
          <Button variant="default" onClick={onClose}>
            {t('vesselCertificates.dialogs.cancel')}
          </Button>
          <Button type="submit" loading={form.formState.isSubmitting}>
            {t('vesselCertificates.dialogs.survey.save')}
          </Button>
        </div>
      </form>
    </Drawer>
  );
}

function useDrawerState(opened: boolean) {
  const [error, setError] = useState<unknown>(null);
  const [confirm, setConfirm] = useState(false);
  useEffect(() => {
    if (opened) {
      setError(null);
      setConfirm(false);
    }
  }, [opened]);
  return { error, setError, confirm, setConfirm };
}

/* ---------- Sörvey ---------- */

export interface SurveyValues {
  kind: string;
  lastDoneOn: string | null;
  nextDueOn: string | null;
  windowFrom: string | null;
  windowTo: string | null;
  overrideReason: string;
  remarks: string;
  [key: string]: unknown;
}

export function surveyValues(survey: VesselSurvey | null, kind: SurveyKind | null): SurveyValues {
  return {
    kind: survey?.kind ?? kind ?? 'annual',
    lastDoneOn: survey?.lastDoneOn ?? null,
    nextDueOn: survey?.nextDueOn ?? null,
    windowFrom: survey?.windowFrom ?? null,
    windowTo: survey?.windowTo ?? null,
    overrideReason: survey?.overrideReason ?? '',
    remarks: survey?.remarks ?? '',
  };
}

/**
 * Survey Schedule satırı (ofis; K29; ADR 0029 D4): pencere boş bırakılırsa vadeden hesaplanır;
 * hesaplanandan farklı pencere gerekçe ister.
 */
export function SurveyDrawer({
  opened,
  survey,
  kind,
  canRemove,
  onClose,
  onSave,
  onRemove,
}: {
  opened: boolean;
  survey: VesselSurvey | null;
  kind: SurveyKind | null;
  canRemove: boolean;
  onClose: () => void;
  onSave: (input: SurveyInput, current: VesselSurvey | null) => Promise<void>;
  onRemove: (survey: VesselSurvey) => Promise<void>;
}) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`vesselCertificates.dialogs.survey.${key}`, values);
  const form = useSchemaForm<SurveyValues>(surveyFormSchema, surveyValues(survey, kind));
  const errorOf = useErrorOf();
  const state = useDrawerState(opened);
  const { errors } = form.formState;
  // biome-ignore lint/correctness/useExhaustiveDependencies: açılışta sıfırlanır
  useEffect(() => {
    if (opened) form.reset(surveyValues(survey, kind));
  }, [opened, survey?.id, kind]);
  const values = form.watch();
  const computed = surveyScheduleWindow(values.kind as SurveyKind, values.nextDueOn ?? null);
  const submit = form.handleSubmit(async (raw) => {
    state.setError(null);
    try {
      await onSave(surveyFormSchema.parse(raw) as SurveyInput, survey);
    } catch (failure) {
      state.setError(failure);
    }
  });
  const date = (name: keyof SurveyValues & string, label: string) => (
    <Controller
      control={form.control}
      name={name}
      render={({ field }) => (
        <DayInput
          label={label}
          clearable
          value={field.value as string | null}
          onChange={field.onChange}
          error={errorOf(errors, name)}
        />
      )}
    />
  );
  return (
    <>
      <OfficeDrawer
        opened={opened}
        title={survey ? k('editTitle') : k('addTitle')}
        form={form}
        error={state.error}
        removeLabel={k('remove')}
        onRemove={survey && canRemove ? () => state.setConfirm(true) : undefined}
        onClose={onClose}
        onSubmit={() => void submit()}
      >
        <Controller
          control={form.control}
          name="kind"
          render={({ field }) => (
            <Select
              label={k('kind')}
              required
              data={SURVEY_KINDS.map((value) => ({
                value,
                label: t(`vesselCertificates.surveys.kinds.${value}`),
              }))}
              value={field.value}
              allowDeselect={false}
              onChange={(value) => field.onChange(value ?? 'annual')}
              comboboxProps={{ withinPortal: true }}
            />
          )}
        />
        <SimpleGrid cols={2}>
          {date('lastDoneOn', k('lastDoneOn'))}
          {date('nextDueOn', k('nextDueOn'))}
          {date('windowFrom', k('windowFrom'))}
          {date('windowTo', k('windowTo'))}
        </SimpleGrid>
        <p className={classes.muted}>
          {computed
            ? k('computed', { from: formatDay(computed.from), to: formatDay(computed.to) })
            : null}{' '}
          {k('overrideHint')}
        </p>
        <Textarea
          label={k('overrideReason')}
          autosize
          minRows={2}
          {...form.register('overrideReason')}
          error={errorOf(errors, 'overrideReason')}
        />
        <Textarea label={k('remarks')} autosize minRows={2} {...form.register('remarks')} />
      </OfficeDrawer>
      <ConfirmRemove
        opened={state.confirm}
        title={k('remove')}
        body={t(`vesselCertificates.surveys.kinds.${survey?.kind ?? 'other'}`)}
        confirm={k('remove')}
        onClose={() => state.setConfirm(false)}
        onConfirm={async () => {
          if (!survey) return;
          await onRemove(survey);
          state.setConfirm(false);
        }}
      />
    </>
  );
}

/* ---------- Klas bulgusu ---------- */

export interface FindingValues {
  kind: string;
  number: string;
  text: string;
  imposedOn: string | null;
  dueOn: string | null;
  closedOn: string | null;
  equipmentId: string | null;
  [key: string]: unknown;
}

export function findingValues(finding: ClassFinding | null, today: string): FindingValues {
  return {
    kind: finding?.kind ?? 'coc',
    number: finding?.number ?? '',
    text: finding?.text ?? '',
    imposedOn: finding?.imposedOn ?? today,
    dueOn: finding?.dueOn ?? null,
    closedOn: finding?.closedOn ?? null,
    equipmentId: finding?.equipmentId ?? null,
  };
}

/**
 * Klas bulgusu (ofis; K25; ADR 0029 D14): CoC, memorandum ya da tavsiye; ekipmana bağlı açık ve
 * gecikmiş CoC ekipmanı PMS dışı yapar.
 */
export function ClassFindingDrawer({
  opened,
  finding,
  today,
  equipment,
  canRemove,
  onClose,
  onSave,
  onRemove,
}: {
  opened: boolean;
  finding: ClassFinding | null;
  today: string;
  equipment: readonly EquipmentChoice[] | null;
  canRemove: boolean;
  onClose: () => void;
  onSave: (input: ClassFindingInput, current: ClassFinding | null) => Promise<void>;
  onRemove: (finding: ClassFinding) => Promise<void>;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`vesselCertificates.dialogs.finding.${key}`);
  const schema = findingFormSchema(today);
  const form = useSchemaForm<FindingValues>(schema, findingValues(finding, today));
  const errorOf = useErrorOf();
  const state = useDrawerState(opened);
  const { errors } = form.formState;
  // biome-ignore lint/correctness/useExhaustiveDependencies: açılışta sıfırlanır
  useEffect(() => {
    if (opened) form.reset(findingValues(finding, today));
  }, [opened, finding?.id]);
  const submit = form.handleSubmit(async (raw) => {
    state.setError(null);
    try {
      await onSave(schema.parse(raw) as ClassFindingInput, finding);
    } catch (failure) {
      state.setError(failure);
    }
  });
  const date = (name: keyof FindingValues & string, label: string, extra = {}) => (
    <Controller
      control={form.control}
      name={name}
      render={({ field }) => (
        <DayInput
          label={label}
          clearable
          value={field.value as string | null}
          onChange={field.onChange}
          error={errorOf(errors, name)}
          {...extra}
        />
      )}
    />
  );
  return (
    <>
      <OfficeDrawer
        opened={opened}
        title={finding ? k('editTitle') : k('addTitle')}
        form={form}
        error={state.error}
        removeLabel={k('remove')}
        onRemove={finding && canRemove ? () => state.setConfirm(true) : undefined}
        onClose={onClose}
        onSubmit={() => void submit()}
      >
        <SimpleGrid cols={2}>
          <Controller
            control={form.control}
            name="kind"
            render={({ field }) => (
              <Select
                label={k('kind')}
                required
                data={CLASS_FINDING_KINDS.map((value) => ({
                  value,
                  label: t(`vesselCertificates.regulatory.findings.kinds.${value}`),
                }))}
                value={field.value}
                allowDeselect={false}
                onChange={(value) => field.onChange(value ?? 'coc')}
                comboboxProps={{ withinPortal: true }}
              />
            )}
          />
          <TextInput
            label={k('number')}
            required
            {...form.register('number')}
            error={errorOf(errors, 'number')}
          />
        </SimpleGrid>
        <Textarea
          label={k('text')}
          required
          autosize
          minRows={3}
          {...form.register('text')}
          error={errorOf(errors, 'text')}
        />
        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          {date('imposedOn', k('imposedOn'), { required: true, clearable: false, maxDate: today })}
          {date('dueOn', k('dueOn'))}
          {date('closedOn', k('closedOn'), { maxDate: today })}
        </SimpleGrid>
        <Controller
          control={form.control}
          name="equipmentId"
          render={({ field }) => (
            <Select
              label={k('equipment')}
              description={k('equipmentHint')}
              searchable
              disabled={equipment === null}
              data={[
                { value: '', label: k('equipmentNone') },
                ...(equipment ?? []).map((e) => ({ value: e.id, label: `${e.code} · ${e.name}` })),
              ]}
              value={field.value ?? ''}
              allowDeselect={false}
              onChange={(value) => field.onChange(value ? value : null)}
              error={errorOf(errors, 'equipmentId')}
              comboboxProps={{ withinPortal: true }}
            />
          )}
        />
      </OfficeDrawer>
      <ConfirmRemove
        opened={state.confirm}
        title={k('remove')}
        body={finding?.number ?? ''}
        confirm={k('remove')}
        onClose={() => state.setConfirm(false)}
        onConfirm={async () => {
          if (!finding) return;
          await onRemove(finding);
          state.setConfirm(false);
        }}
      />
    </>
  );
}

/* ---------- Başmühendis yetki beyanı ---------- */

export interface AuthorizationValues {
  userId: string | null;
  validFrom: string | null;
  validTo: string | null;
  declarationRef: string;
  [key: string]: unknown;
}

export function authorizationValues(authorization: CeAuthorization | null): AuthorizationValues {
  return {
    userId: authorization?.userId ?? null,
    validFrom: authorization?.validFrom ?? null,
    validTo: authorization?.validTo ?? null,
    declarationRef: authorization?.declarationRef ?? '',
  };
}

/**
 * Başmühendisin yetki beyanı (ofis; K31; NR467 Pt A Ch 2 App 2): kişi şirkette olmalı, gemide
 * görev almamış olabilir.
 */
export function CeAuthorizationDrawer({
  opened,
  authorization,
  people,
  canRemove,
  onClose,
  onSave,
  onRemove,
}: {
  opened: boolean;
  authorization: CeAuthorization | null;
  people: readonly PersonChoice[] | null;
  canRemove: boolean;
  onClose: () => void;
  onSave: (input: CeAuthorizationInput, current: CeAuthorization | null) => Promise<void>;
  onRemove: (authorization: CeAuthorization) => Promise<void>;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`vesselCertificates.dialogs.authorization.${key}`);
  const form = useSchemaForm<AuthorizationValues>(
    authorizationFormSchema,
    authorizationValues(authorization),
  );
  const errorOf = useErrorOf();
  const state = useDrawerState(opened);
  const { errors } = form.formState;
  // biome-ignore lint/correctness/useExhaustiveDependencies: açılışta sıfırlanır
  useEffect(() => {
    if (opened) form.reset(authorizationValues(authorization));
  }, [opened, authorization?.id]);
  const submit = form.handleSubmit(async (raw) => {
    state.setError(null);
    try {
      await onSave(authorizationFormSchema.parse(raw) as CeAuthorizationInput, authorization);
    } catch (failure) {
      state.setError(failure);
    }
  });
  const choices = [...(people ?? [])];
  if (authorization && !choices.some((p) => p.id === authorization.userId)) {
    choices.push({ id: authorization.userId, name: authorization.userName ?? '—', role: null });
  }
  return (
    <>
      <OfficeDrawer
        opened={opened}
        title={authorization ? k('editTitle') : k('addTitle')}
        form={form}
        error={state.error}
        removeLabel={k('remove')}
        onRemove={authorization && canRemove ? () => state.setConfirm(true) : undefined}
        onClose={onClose}
        onSubmit={() => void submit()}
      >
        <p className={classes.muted}>{k('hint')}</p>
        <Controller
          control={form.control}
          name="userId"
          render={({ field }) => (
            <Select
              label={k('person')}
              required
              searchable
              disabled={people === null}
              data={choices.map((p) => ({
                value: p.id,
                label: p.role ? `${p.name} · ${p.role}` : p.name,
              }))}
              value={field.value}
              onChange={field.onChange}
              error={errorOf(errors, 'userId')}
              comboboxProps={{ withinPortal: true }}
            />
          )}
        />
        <SimpleGrid cols={2}>
          {(['validFrom', 'validTo'] as const).map((name) => (
            <Controller
              key={name}
              control={form.control}
              name={name}
              render={({ field }) => (
                <DayInput
                  label={k(name)}
                  required={name === 'validFrom'}
                  clearable={name === 'validTo'}
                  value={field.value as string | null}
                  onChange={field.onChange}
                  error={errorOf(errors, name)}
                />
              )}
            />
          ))}
        </SimpleGrid>
        <TextInput
          label={k('declarationRef')}
          required
          {...form.register('declarationRef')}
          error={errorOf(errors, 'declarationRef')}
        />
      </OfficeDrawer>
      <ConfirmRemove
        opened={state.confirm}
        title={k('remove')}
        body={authorization?.declarationRef ?? ''}
        confirm={k('remove')}
        onClose={() => state.setConfirm(false)}
        onConfirm={async () => {
          if (!authorization) return;
          await onRemove(authorization);
          state.setConfirm(false);
        }}
      />
    </>
  );
}
